import { composeRefs } from "@prismicio/editor-support/React"
import { clsx } from "clsx"
import {
  forwardRef,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useRef,
  useState,
} from "react"

import type { SX } from "../../theme"
import { Icon, type IconName } from "../Icon"
import { IconButton } from "../IconButton"
import { Text } from "../Text"
import styles from "./Badge.module.css"

export interface BadgeProps {
  title: ReactNode
  color?: "grey" | "green" | "amber" | "purple" | "tomato" | "indigo"
  size?: "large" | "medium" | "small"
  maxWidth?: 150 | 250
  icon?: IconName
  selected?: boolean
  sx?: SX
  onClick?: (event?: MouseEvent<HTMLDivElement>) => void
  onClose?: () => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  /** Makes the badge content editable, fires onBlur when the value has changed */
  onEdit?: (newTitle: string) => void | Promise<void>
}

export const Badge = forwardRef<HTMLDivElement, BadgeProps>(
  function Badge(props, ref) {
    const {
      title,
      color = "grey",
      size = "large",
      maxWidth,
      icon,
      selected = false,
      sx,
      onClick,
      onClose,
      onMouseEnter,
      onMouseLeave,
      onEdit,
      ...otherProps
    } = props

    const editable = Boolean(onEdit)

    const clickable = Boolean(onClick) || editable

    const className = clsx(
      styles.root,
      styles[`color-${color}`],
      styles[`size-${size}`],
      maxWidth && styles[`width-${maxWidth}`],
      !clickable && styles.disablePointerEvents,
      selected && styles.selected,
      editable && styles.editable,
    )

    const badgeElementRef = useRef<HTMLDivElement>(null)
    const [key, setKey] = useState(false)

    async function onEditFinish() {
      if (!onEdit) return

      const value = badgeElementRef.current?.innerText || ""

      if (value !== title && value.trim() !== "") {
        await onEdit(value)
      }

      setKey((prev) => !prev)
    }

    function onKeyDown(event: KeyboardEvent) {
      event.stopPropagation()

      if (event.code === "Enter" && editable) {
        void onEditFinish()
        return
      }

      if (event.code === "Enter" || event.code === "Space") {
        onClick?.()
      }
    }

    const content =
      typeof title === "string" ? (
        <Text
          variant={selected ? "smallBold" : "small"}
          color={`${color}11`}
          noWrap
        >
          {title}
        </Text>
      ) : (
        title
      )

    return (
      <div
        {...otherProps}
        key={String(key)}
        contentEditable={editable}
        suppressContentEditableWarning
        onBlur={onEditFinish}
        role="button"
        tabIndex={clickable ? 0 : -1}
        className={className}
        style={sx}
        onClick={onClick}
        onKeyDown={onKeyDown}
        data-marker="badge"
        ref={composeRefs(ref, badgeElementRef)}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {icon && <Icon name={icon} size="extraSmall" color={`${color}11`} />}
        {content}
        {editable && (
          <Icon name="edit" size="extraSmall" color={`${color}11`} />
        )}
        {onClose && (
          <div className={styles.enablePointerEvents}>
            <IconButton
              icon="close"
              size="small"
              color={color}
              hasPadding={false}
              onClick={onClose}
            />
          </div>
        )}
      </div>
    )
  },
)
